import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getPerson, getPersonWishes } from '../services/people';
import AppLayout from '../components/AppLayout';
import Avatar from '../components/Avatar';
import WishGiftCard from '../components/WishGiftCard';
import SuggestIdeaModal from '../components/SuggestIdeaModal';
import SurpriseGiftModal from '../components/SurpriseGiftModal';
import { nextBirthday, countdownLabel, revealAfterBirthday } from '../utils/dates';

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

/**
 * PersonDetail — one person's profile and their wish list. Reached by tapping a
 * member in a group's Members tab. The people counterpart of PetDetail.
 *
 * `?group=` (optional) scopes the list to the group you arrived from, so opening
 * someone from Family shows what they shared with Family rather than mixing in
 * every other group you happen to share. The API re-checks your membership of
 * that group, so the parameter can only ever shrink what you'd see anyway.
 *
 * OWNER-BLINDNESS: this page also renders YOUR OWN profile (tapping yourself in
 * the Members list). The API keeps your purchased wishes present but masked, and
 * WishGiftCard takes the owner branch off `wish.user_id`, so no claim/purchase
 * state or gifting button is ever drawn for your own items.
 */
export default function PersonDetail() {
    const { userId } = useParams();
    const [searchParams] = useSearchParams();
    const groupId = searchParams.get('group');
    const { user } = useAuth();

    const [person, setPerson] = useState(null);
    const [wishes, setWishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [flash, setFlash] = useState(null); // { type, text }
    const [busyId, setBusyId] = useState(null);

    // Purchase modal state (gifter view).
    const [purchaseTarget, setPurchaseTarget] = useState(null);
    const [purchaseNote, setPurchaseNote] = useState('');
    const [revealDate, setRevealDate] = useState('');

    // "Suggest an idea" compose modal.
    const [showSuggest, setShowSuggest] = useState(false);
    // "Add a surprise gift" compose modal — a wish for this person, hidden from them.
    const [showSurprise, setShowSurprise] = useState(false);

    const isSelf = String(user?.id) === String(userId);

    const notify = useCallback((type, text) => {
        setFlash({ type, text });
        if (type === 'success') setTimeout(() => setFlash(null), 3000);
    }, []);

    const reloadWishes = useCallback(async () => {
        // Callers fire this bare after a claim/purchase; own the failure here so a
        // failed refetch can't reject unhandled or silently leave a stale list.
        try {
            setWishes(await getPersonWishes(userId, groupId));
        } catch {
            notify('error', 'Updated, but the list could not refresh. Reload the page.');
        }
    }, [userId, groupId, notify]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const [personData, wishesData] = await Promise.all([
                    getPerson(userId),
                    getPersonWishes(userId, groupId),
                ]);
                if (cancelled) return;
                setPerson(personData);
                setWishes(wishesData);
            } catch (err) {
                if (cancelled) return;
                // Only a genuine 403/404 means "no such person, or not someone you
                // share a group with". A transient failure must not masquerade as
                // that — show a retryable error instead.
                const status = err?.response?.status;
                if (status === 403 || status === 404) {
                    setNotFound(true);
                } else {
                    notify('error', errText(err, 'Could not load this person. Please try again.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // notify is a stable useCallback; the effect keys on who/where we're viewing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, groupId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    if (notFound || !person) {
        return (
            <AppLayout>
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    This person could not be found, or you don't share a group with them.
                </div>
            </AppLayout>
        );
    }

    const bday = nextBirthday(person.birthday);

    // Which group to claim/purchase within. Prefer the group we're scoped to;
    // otherwise the first group the wish is shared into that we can see (the API
    // already narrowed `groups` to the viewer's own).
    const groupIdFor = (wish) => (groupId ? Number(groupId) : wish.groups?.[0]?.id);

    const claim = async (wish) => {
        setBusyId(wish.id);
        try {
            await api.post(`/wishes/${wish.id}/claim`, { group_id: groupIdFor(wish) });
            notify('success', 'Claimed — others now see it as reserved.');
            await reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not claim the wish.'));
        } finally {
            setBusyId(null);
        }
    };

    const unclaim = async (wish) => {
        setBusyId(wish.id);
        try {
            await api.delete(`/wishes/${wish.id}/claim`);
            notify('success', 'Claim released.');
            await reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not release the claim.'));
        } finally {
            setBusyId(null);
        }
    };

    const openPurchase = (wish) => {
        setPurchaseTarget(wish);
        setPurchaseNote('');
        const reveal = revealAfterBirthday(person.birthday);
        setRevealDate(reveal ? reveal.iso : '');
    };

    const submitPurchase = async (e) => {
        e.preventDefault();
        const wish = purchaseTarget;
        setBusyId(wish.id);
        try {
            await api.post(`/wishes/${wish.id}/purchase`, {
                group_id: groupIdFor(wish),
                private_note: purchaseNote || null,
                reveal_date: revealDate || null,
            });
            notify('success', 'Marked as purchased. The owner will not see this.');
            setPurchaseTarget(null);
            await reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not record the purchase.'));
        } finally {
            setBusyId(null);
        }
    };

    const undoPurchase = async (wish) => {
        setBusyId(wish.id);
        try {
            await api.delete(`/wishes/${wish.id}/purchase`);
            notify('success', 'Purchase undone.');
            await reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not undo the purchase.'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <AppLayout>
            {flash && (
                <div
                    className={`mb-4 p-3 rounded-md text-sm border ${
                        flash.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                >
                    {flash.text}
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-lg shadow p-5 mb-6 flex items-center gap-4">
                <Avatar
                    name={person.name}
                    url={person.avatar_url}
                    color={person.avatar_color}
                    seed={person.id}
                    size={64}
                />
                <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {person.name}
                        {isSelf && <span className="ml-2 text-sm font-normal text-gray-400">(You)</span>}
                    </h2>
                    {bday && (
                        <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                            🎂 {bday.label} · {countdownLabel(bday.daysUntil)}
                        </span>
                    )}
                </div>
                {/* Send surfaces. Both hidden on your own profile (you can't
                    suggest or surprise yourself; the API 422s either). "Suggest an
                    idea" asks them; "Add a surprise gift" hides a wish FROM them. */}
                {!isSelf && (
                    <div className="flex flex-wrap gap-2 justify-end">
                        <button
                            onClick={() => setShowSuggest(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
                        >
                            Recommend a gift
                        </button>
                        <button
                            onClick={() => setShowSurprise(true)}
                            className="px-4 py-2 border border-amber-300 bg-amber-50 text-amber-800 rounded-md hover:bg-amber-100 text-sm font-medium whitespace-nowrap"
                            title={`Add a wish for ${person.name}, hidden from them`}
                        >
                            Add a surprise gift 🎁
                        </button>
                    </div>
                )}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {isSelf ? 'Your wishes' : `${person.name}'s wishes`}
            </h3>

            {wishes.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    {isSelf
                        ? "You haven't shared any wishes into your groups yet."
                        : `${person.name} hasn't shared any wishes you can see yet.`}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {wishes.map((wish) => (
                        <WishGiftCard
                            key={wish.id}
                            wish={wish}
                            user={user}
                            busy={busyId === wish.id}
                            onClaim={() => claim(wish)}
                            onUnclaim={() => unclaim(wish)}
                            onPurchase={() => openPurchase(wish)}
                            onUndoPurchase={() => undoPurchase(wish)}
                            notify={notify}
                            onShareChanged={reloadWishes}
                        />
                    ))}
                </div>
            )}

            {/* Purchase modal */}
            {purchaseTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-1">Mark as purchased</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            “{purchaseTarget.title}”. The owner will never see this.
                        </p>
                        <form onSubmit={submitPurchase} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reveal date (optional)
                                </label>
                                <input
                                    type="date"
                                    value={revealDate}
                                    onChange={(e) => setRevealDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    {revealAfterBirthday(person.birthday)
                                        ? `Defaulted to the day after ${person.name}'s birthday, so the surprise lasts through it. `
                                        : ''}
                                    On this date the owner may be shown who bought it.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Private note (optional)
                                </label>
                                <textarea
                                    value={purchaseNote}
                                    onChange={(e) => setPurchaseNote(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    rows={2}
                                    placeholder="e.g., Ordered, arrives Dec 20"
                                />
                            </div>
                            <div className="flex gap-2 justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={() => setPurchaseTarget(null)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                                    Confirm Purchase
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Suggest-an-idea compose modal — recipient + group prefilled. */}
            {showSuggest && (
                <SuggestIdeaModal
                    prefill={{
                        recipientUserId: Number(userId),
                        groupId: groupId ? Number(groupId) : undefined,
                    }}
                    notify={notify}
                    onClose={() => setShowSuggest(false)}
                    onSaved={() => setShowSuggest(false)}
                />
            )}

            {/* Surprise-gift compose modal — a wish FOR this person, hidden from
                them. Recipient is fixed; the group defaults to the one we're
                scoped to. On save, refresh so the new surprise shows in the list
                (we're a non-recipient, so we can see it). */}
            {showSurprise && (
                <SurpriseGiftModal
                    recipient={{ id: Number(userId), name: person.name }}
                    prefill={{ groupId: groupId ? Number(groupId) : undefined }}
                    notify={notify}
                    onClose={() => setShowSurprise(false)}
                    onSaved={() => {
                        setShowSurprise(false);
                        reloadWishes();
                    }}
                />
            )}
        </AppLayout>
    );
}
